import sortBy from "lodash/sortBy";
import { getRoleFormKeyPrefix } from "PFApp/components/activity_form/multi_activity_form/tabular_roles_form/tabular_roles_form.utils";
import {
  FIELD_SUBTEMPLATE_KEY,
  FORM_ROLES_KEY,
  FORM_SUBTEMPLATES_KEY,
  NO_SUBTEMPLATE_VALUE
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { FormSchemaField, RolesFormKey } from "PFApp/types/activity_form/activity_form_schema";
import {
  ManageRolesFormType,
  ManageRolesMultiForm
} from "PFApp/types/activity_form/manage_roles_form_schema";
import { Id } from "PFCore/types";

export const groupRolesIntoForm = (roles: ManageRolesFormType[]): ManageRolesMultiForm => {
  const subtemplateMap = new Map<string, ManageRolesFormType[]>();

  for (const role of roles) {
    const key = role.subtemplate_key ?? NO_SUBTEMPLATE_VALUE;
    if (!subtemplateMap.has(key)) {
      subtemplateMap.set(key, []);
    }
    subtemplateMap.get(key)!.push(role);
  }

  return {
    [FORM_SUBTEMPLATES_KEY]: Array.from(subtemplateMap.entries()).map(([key, subtemplateRoles]) => ({
      [FIELD_SUBTEMPLATE_KEY]: key,
      [FORM_ROLES_KEY]: sortBy(subtemplateRoles, ["engagement_id", "name"])
    }))
  };
};

const MANAGE_ROLES_BASE_COLUMNS_ORDER = [
  "engagement_name",
  "name",
  "original_wm",
  "suggested_wm",
  "booked_wm"
] as const;

export const getManageRolesColumnsOrder = (selectedColumnNames: string[]): string[] => {
  const anchorIndex = MANAGE_ROLES_BASE_COLUMNS_ORDER.indexOf("name");

  return [
    ...MANAGE_ROLES_BASE_COLUMNS_ORDER.slice(0, anchorIndex + 1),
    ...selectedColumnNames,
    ...MANAGE_ROLES_BASE_COLUMNS_ORDER.slice(anchorIndex + 1)
  ];
};

export const mergeManageRolesColumns = <T extends { id?: string }, U extends { id?: string }>(
  schemaColumns: T[],
  customColumns: U[],
  columnsOrder: readonly string[]
): (T | U)[] => {
  const allColumns = [...customColumns, ...schemaColumns];
  const columnMap = new Map(allColumns.filter((col) => col.id !== undefined).map((col) => [col.id!, col]));

  const orderedColumns: (T | U)[] = [];

  // First: add explicitly ordered columns that exist
  for (const id of columnsOrder) {
    const col = columnMap.get(id);
    if (col) {
      orderedColumns.push(col);
      columnMap.delete(id);
    }
  }

  // Then: append remaining schema columns not in the order list
  for (const col of schemaColumns) {
    if (col.id !== undefined && columnMap.has(col.id)) {
      orderedColumns.push(col);
      columnMap.delete(col.id);
    }
  }

  return orderedColumns;
};

export const getTrackedKeysFromSchemaFields = (schemaFields: FormSchemaField[]): Set<string> =>
  new Set(schemaFields.map((field) => `${getRoleFormKeyPrefix(field)}${field.name}`));

export const parseJsonArrayIds = (value: string | null): number[] => {
  if (!value) {
    return [];
  }
  try {
    const parsed = JSON.parse(value);

    if (Array.isArray(parsed)) {
      return parsed.filter((item): item is number => typeof item === "number" && item > 0);
    }
  } catch {
    // ignore malformed JSON
  }
  return [];
};

export const getNestedRoles = (
  values: ManageRolesMultiForm | undefined,
  rolesFormKey: RolesFormKey
): ManageRolesFormType[] | undefined => {
  const [, indexStr] = rolesFormKey.split(".");
  return values?.[FORM_SUBTEMPLATES_KEY]?.[Number(indexStr)]?.[FORM_ROLES_KEY];
};

export const buildRoleMap = (formValues: ManageRolesMultiForm): Map<Id, ManageRolesFormType> => {
  const map = new Map<Id, ManageRolesFormType>();
  for (const subtemplate of formValues.subtemplates ?? []) {
    for (const role of subtemplate.roles ?? []) {
      if (role.id !== undefined) {
        map.set(role.id, role);
      }
    }
  }
  return map;
};

export const findRoleFormPath = (
  formValues: ManageRolesMultiForm,
  roleId: Id
): { subtemplateIndex: number; roleIndex: number } | undefined => {
  const subtemplates = formValues.subtemplates ?? [];

  const subtemplateIndex = subtemplates.findIndex((subtemplate) =>
    (subtemplate.roles ?? []).some((role) => role.id === roleId)
  );

  if (subtemplateIndex === -1) {
    return undefined;
  }

  const roleIndex = (subtemplates[subtemplateIndex].roles ?? []).findIndex((role) => role.id === roleId);

  return { subtemplateIndex, roleIndex };
};
